import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import Button from 'components/Button/Button';
import IconButton from 'components/IconButton/IconButton';
import RichCard from 'components/RichCard/RichCard';

import CreateLinkPopup from './CreateLinkPopup';

import Delete24 from 'icons/Delete24';
import Edit24 from 'icons/Edit24';
import ReceiptLong24 from 'icons/ReceiptLong24';

import { paymentPeriods } from 'utils/constants';
import formatAmount from 'utils/formatAmount';
import { actions } from 'utils/permissions';

/**
 * Payment Page Card component to render in the payment pages list.
 *
 * @component
 * @example
 * const data = {
 *  title: "My Page",
 *  is_recurrent: false,
 *  default_regular_amount: 1000
 * }
 * <PaymentPageCard data={data} />
 * @param {object} props Properties.
 * @returns
 */
export default function ProductCard(props) {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { data, onClick, selected, onLinkCopied, onDelete, terminalType } = props;
    const { id, title, is_recurrent, default_regular_amount: price } = data;

    const [popup, setPopup] = React.useState(null);

    /**
     * Generates description text for payment card.
     * @returns {string} Payment card description.
     */
    function generateDescription() {
        const pageType = is_recurrent ? t('products.type.subscription') : t('products.type.payment');
        const currency = is_recurrent ? `₽/${paymentPeriods[data.once_per_days]}` : '₽';
        const terminal = terminalType === 'Tinkoff' ? 'Тинькофф' : 'Юкасса';

        return `${pageType}, ${formatAmount(price)} ${currency}, ${terminal}`;
    }

    function handleEdit() {
        navigate('/pages/' + id, { state: { pageId: id, editPageData: data } });
    }

    function handleDelete() {
        if (typeof onDelete === 'function') {
            onDelete(id);
        }
    }

    function handlePopupClosed() {
        setPopup(null);
    }

    function handleCopyLink() {
        setPopup(
            <CreateLinkPopup
                terminalType={terminalType}
                onClosed={handlePopupClosed}
                onLinkCopied={onLinkCopied}
                data={data}
            />
        );
    }

    function handleClick() {
        if (typeof onClick === 'function') {
            onClick(id);
        }
    }

    const pageActions = (
        <div style={{ display: 'flex', paddingTop: 16, gap: 8, flexWrap: 'wrap' }}>
            <Button
                text={t('products.action.createLink')}
                size={'m'}
                icon={<ReceiptLong24 />}
                onClick={handleCopyLink}
                level={'secondary'}
            />
            <IconButton
                icon={<Edit24 />}
                needPermission={actions.EDIT_FORM}
                accent={true}
                onDark={true}
                onClick={handleEdit}
            />
            <IconButton
                icon={<Delete24 />}
                needPermission={actions.DELETE_FORM}
                accent={true}
                onDark={true}
                onClick={handleDelete}
            />
        </div>
    );

    return (
        <>
            {popup}
            <RichCard
                title={title}
                description={generateDescription()}
                selectable={true}
                bottom={selected ? pageActions : null}
                selected={selected}
                onClick={handleClick}
            />
        </>
    );
}

ProductCard.propTypes = {
    /**
     * Page info object.
     */
    data: PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
        is_recurrent: PropTypes.bool,
        default_regular_amount: PropTypes.number.isRequired,
        once_per_days: PropTypes.number,
    }).isRequired,
    onClick: PropTypes.func,
    /**
     * If this page card selected and previewing now.
     */
    selected: PropTypes.bool,
    /**
     * Triggers on page link copied.
     */
    onLinkCopied: PropTypes.func,
    onDelete: PropTypes.func.isRequired,
    terminalType: PropTypes.oneOf(['Tinkoff', 'Yookassa']),
};
